import { useMutation, useQuery } from '@apollo/client'
import CalendarToday from '@mui/icons-material/CalendarToday'
import EventIcon from '@mui/icons-material/Event'
import dayjs from 'dayjs'
import { filter } from 'graphql-anywhere'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Calendar as CalendarComponent, momentLocalizer, stringOrDate } from 'react-big-calendar'
import { Helmet } from 'react-helmet'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import { useTranslation } from 'react-i18next'
import { makeStyles } from 'tss-react/mui'
import AgendaCalendarEventView from './AgendaCalendarEventView'
import CalendarDialog from './CalendarDialog'
import CalendarEventView from './CalendarEventView'
import { ConditionalComponent } from '../../components/ConditionalComponent'
import GraphqlError from '../../components/GraphqlError'
import SpeedDials from '../../components/SpeedDials'
import {
  CalendarEventBasicDataFragment,
  CalendarInput,
  CalendarMutationMutation,
  CalendarMutationMutationVariables,
  EventInput,
  EventMutationMutation,
  EventMutationMutationVariables,
  eventsQuery,
} from '../../generated/graphql'
import DataHelper, { EntityNames } from '../../helpers/DataHelper'
import { useDeleteMutation } from '../../helpers/GqlHelper'
import { useBranchFilter } from '../../hooks/BranchFilterHook'
import { useSelectedCalendars } from '../../providers/CalendarContext'
import { AclAction, useUserContext } from '../../providers/UserContext'
import CalendarEvents from '../../schema/CalendarEvents'
import Calendars from '../../schema/Calendars'
import Recurrence from '../../schema/Recurrence'
import Tags from '../../schema/Tags'
import EventAttendanceRegisterDialog from '../events/EventAttendanceRegisterDialog'
import EventDialog from '../events/EventDialog'
import ViewEventDialog from '../events/ViewEventDialog'

const localizer = momentLocalizer(moment)

type DateRange = null | { start: Date; end: Date }
type UnixRange = { fromUnix: number; toUnix: number }

const useStyles = makeStyles()(() => {
  return {
    root: {
      height: 'calc(100vh - 150px)',
      minHeight: 729,
      paddingBottom: 20,
    },
    baseEvent: {
      padding: 0,
      fontWeight: 300,
      backgroundColor: 'transparent',
    },
    agendaViewItem: {
      cursor: 'pointer',
    },
  }
})

const updateDates = (
  datesRange: DateRange,
  setDatesRange: (range: DateRange) => void,
  range: Date[] | { start: stringOrDate; end: stringOrDate },
) => {
  let newDateRange: DateRange
  if (Array.isArray(range)) {
    const momentDates = range.map((d) => dayjs(d))
    const end = dayjs.max(momentDates)
    const start = dayjs.min(momentDates)
    newDateRange = { start: start.toDate(), end: end.toDate() }
  } else {
    newDateRange = { start: dayjs(range.start).toDate(), end: dayjs(range.end).toDate() }
  }
  let changed: boolean
  if (!datesRange) {
    changed = !!newDateRange
  } else {
    changed =
      dayjs(newDateRange.start).unix() !== dayjs(datesRange.start).unix() ||
      dayjs(newDateRange.end).unix() !== dayjs(datesRange.end).unix()
  }
  if (changed) {
    // Set dates from midnight to midnight
    setDatesRange({
      start: dayjs(newDateRange.start).startOf('day').toDate(),
      end: dayjs(newDateRange.end).endOf('day').toDate(),
    })
  }
}

const CalendarScreen: React.FC = () => {
  const calendar = useRef<any>(null)
  const initialNowRef = useRef<Date>(new Date())
  const { classes } = useStyles()
  const [datesRange, setDatesRange] = useState<null | { start: Date; end: Date }>(null)
  useEffect(() => {
    if (calendar.current) {
      const ref = calendar.current
      // Initial date ranges
      if (ref) {
        const v = ref.getView()
        if (v) {
          const newRange = v.range(ref.props.getNow(), { localizer: ref.props.localizer }, ref.props.view)
          updateDates(null, setDatesRange, newRange)
        }
      }
    }
  }, [calendar, setDatesRange])
  const [newCalendarOpen, setNewCalendarOpen] = useState(false)
  const user = useUserContext()
  const [newEventOpen, setNewEventOpen] = useState<Partial<EventInput> | undefined>(undefined)
  const [inViewEventId, setInViewEventId] = useState<string | null>(null)
  const [inEditEventId, setInEditEventId] = useState<string | null>(null)
  const [showAttendanceManagementDialog, setShowAttendanceManagementDialog] = useState(false)
  const branchFilter = useBranchFilter()
  const { enqueueSnackbar } = useSnackbar()
  const [newCalendar] = useMutation<CalendarMutationMutation, CalendarMutationMutationVariables>(
    Calendars.EDIT_CALENDAR,
    {
      refetchQueries: [{ query: Calendars.GET_CALENDARS, variables: { branchFilter } }],
    },
  )

  const [mutateEvent] = useMutation<EventMutationMutation, EventMutationMutationVariables>(CalendarEvents.EDIT_EVENT)

  const [deleteEvent] = useDeleteMutation(CalendarEvents.GET_EVENTS, { branchFilter })
  const { t } = useTranslation()

  const { fromUnix, toUnix } = useMemo<UnixRange>(() => {
    const start = (datesRange?.start ?? initialNowRef.current) as Date
    const end = (datesRange?.end ?? initialNowRef.current) as Date
    return {
      fromUnix: dayjs(start).unix(),
      toUnix: dayjs(end).unix(),
    }
  }, [datesRange])

  const { error, data } = useQuery<eventsQuery>(CalendarEvents.GET_EVENTS, {
    variables: {
      fromDate: fromUnix,
      toDate: toUnix,
      branchFilter,
    },
  })

  const canCreateEvent = user.canDo(EntityNames.CalendarEvent, AclAction.create)
  const canCreateCalendar = user.canDo(EntityNames.Calendar, AclAction.create)

  const dialActions = useMemo(() => {
    const actions: { icon: ReactElement; name: string; action: () => any }[] = []
    if (canCreateEvent) {
      actions.push({
        icon: <EventIcon />,
        name: 'New event',
        action: () => setNewEventOpen({}),
      })
    }

    if (canCreateCalendar) {
      actions.push({
        icon: <CalendarToday />,
        name: 'New calendar',
        action: () => setNewCalendarOpen(true),
      })
    }

    return actions
  }, [canCreateCalendar, canCreateEvent])

  const selectedCalendars = useSelectedCalendars()
  const selectedCalendarIds = useMemo(() => new Set(selectedCalendars.map((cal) => cal.id)), [selectedCalendars])

  const events = useMemo<CalendarEventBasicDataFragment[]>(() => {
    const allEvents: CalendarEventBasicDataFragment[] = data?.events ?? []
    if (selectedCalendarIds.size === 0) return []
    return allEvents.filter((e) => selectedCalendarIds.has(e.id_calendar))
  }, [data?.events, selectedCalendarIds])

  const inViewEvent = useMemo(
    () => (inViewEventId ? events.find((ev) => ev.id === inViewEventId) ?? null : null),
    [events, inViewEventId],
  )
  useEffect(() => {
    if (inViewEventId && !inViewEvent) setInViewEventId(null)
  }, [inViewEvent, inViewEventId])

  const createCalendar = useCallback(
    async (input: CalendarInput) => {
      await newCalendar({
        variables: { input },
      })
    },
    [newCalendar],
  )

  const onRangeChange = useCallback(
    (range: Date[] | { start: stringOrDate; end: stringOrDate }) => {
      updateDates(datesRange, setDatesRange, range)
    },
    [datesRange],
  )

  const onSelectEvent = useCallback((event: any) => setInViewEventId(event?.id ?? null), [])
  const onAgendaEventClick = useCallback((event: any) => setInViewEventId(event?.id ?? null), [])

  const startAccessor = useCallback((event: any) => dayjs.unix(event.start).toDate(), [])
  const endAccessor = useCallback((event: any) => dayjs.unix(event.end).toDate(), [])
  const eventPropGetter = useCallback(() => ({ className: classes.baseEvent }), [classes.baseEvent])

  const components = useMemo(() => {
    return {
      event: CalendarEventView,
      agenda: {
        event: (props: any) => {
          return (
            <div className={classes.agendaViewItem} onClick={() => onAgendaEventClick(props.event)}>
              <AgendaCalendarEventView {...props} />
            </div>
          )
        },
      },
    }
  }, [classes.agendaViewItem, onAgendaEventClick])

  const createEvent = useCallback(
    async (input: EventInput & Partial<CalendarEventBasicDataFragment>) => {
      const recurrenceData = {
        recurrence: null,
      }
      if (input.recurrence) {
        recurrenceData.recurrence = {
          ...filter(Recurrence.fragments.RecurrenceInput, input.recurrence),
        }
      }

      await mutateEvent({
        variables: {
          input: {
            ...DataHelper.removeTypeName(input),
            ...recurrenceData,
            reminders: input.reminders?.map((r) => ({ offset: r.offset, template: r.template })),
            tags: filter(Tags.fragment.TagData, input.tags ?? []),
          },
        },
        refetchQueries: [
          {
            query: CalendarEvents.GET_EVENTS,
            variables: {
              fromDate: fromUnix,
              toDate: toUnix,
              branchFilter,
            },
          },
        ],
      })
    },
    [branchFilter, fromUnix, mutateEvent, toUnix],
  )

  const deleteNode = useCallback(
    async (id: string) => {
      await deleteEvent({
        variables: {
          input: { id },
        },
      })
    },
    [deleteEvent],
  )

  const editEvent = useCallback(async (event: CalendarEventBasicDataFragment) => {
    setInViewEventId(null)
    setInEditEventId(event.id)
  }, [])

  const manageAttendance = useCallback(() => {
    setShowAttendanceManagementDialog(true)
  }, [])

  const handleSlotSelect = useCallback(
    (slotInfo: {
      start: stringOrDate
      end: stringOrDate
      slots: Date[] | string[]
      action: 'select' | 'click' | 'doubleClick'
    }) => {
      const today = dayjs()
      let start = dayjs(slotInfo.start)
      let end = dayjs(slotInfo.end)
      if (start.hour() === end.hour() && start.minute() === end.minute()) {
        start = start.set('hour', today.hour()).set('minute', today.minute()).set('second', today.second())
        end = start.add(2, 'hour')
      }

      if (!canCreateEvent) return
      // Disallow creating events in the past (keep selection untouched, just warn).
      if (start.isBefore(today.startOf('day'))) {
        enqueueSnackbar(t("You can't create events in the past. Please pick a future date/time."), { variant: 'warning' })
        return
      }

      setNewEventOpen({
        start: start.unix(),
        end: end.unix(),
      })
    },
    [canCreateEvent, enqueueSnackbar, t],
  )

  let errors: ReactElement | null = null
  if (error) {
    errors = <GraphqlError error={error} />
  }
  return (
    <div className={classes.root}>
      <Helmet>
        <title>{t('Calendar')}</title>
      </Helmet>
      {errors}
      <CalendarComponent
        ref={calendar}
        popup
        selectable
        views={{
          month: true,
          week: true,
          day: true,
          agenda: true,
        }}
        components={components}
        localizer={localizer}
        events={events}
        onRangeChange={onRangeChange}
        onSelectEvent={onSelectEvent}
        startAccessor={startAccessor}
        endAccessor={endAccessor}
        tooltipAccessor="description"
        eventPropGetter={eventPropGetter}
        onSelectSlot={handleSlotSelect}
      />
      <ConditionalComponent show={dialActions.length}>
        <SpeedDials actions={dialActions} />
      </ConditionalComponent>

      <CalendarDialog open={newCalendarOpen} onClose={() => setNewCalendarOpen(false)} commit={createCalendar} />
      <EventDialog
        open={!!newEventOpen}
        initData={newEventOpen}
        onClose={() => setNewEventOpen(undefined)}
        commit={createEvent}
      />
      <ConditionalComponent show={inEditEventId}>
        <EventDialog
          open={true}
          id={inEditEventId ?? undefined}
          onClose={() => setInEditEventId(null)}
          commit={createEvent}
        />
      </ConditionalComponent>
      <ConditionalComponent show={inViewEvent}>
        <ViewEventDialog
          open={true}
          event={inViewEvent}
          deleteEvent={deleteNode}
          onClose={() => setInViewEventId(null)}
          manageAttendance={manageAttendance}
          editEvent={() => editEvent(inViewEvent)}
        />
      </ConditionalComponent>
      <ConditionalComponent show={inViewEvent}>
        <EventAttendanceRegisterDialog
          event={inViewEvent}
          editEvent={createEvent}
          open={showAttendanceManagementDialog}
          onClose={() => setShowAttendanceManagementDialog(false)}
        />
      </ConditionalComponent>
    </div>
  )
}

export default CalendarScreen
